import cx from 'classnames';
import styles from './Button.module.scss';

import { FC, ReactNode } from 'react';
import { Typography } from '../Typography';
import { AnimatedDotsText } from '../AnimatedDotsText';
import { BigGoldButtonSvg, ButtonSvg } from '@assets/svg';
import { ChipBingoIcon } from '@assets/icons';

interface ButtonProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  disabled?: boolean;
  onClick: VoidFunction;
  shape?: 'default' | 'square';
  variant:
    | 'text'
    | 'custom'
    | 'custom-stretchable'
    | 'filled'
    | 'outlined'
    | 'custom-gold'
    | 'custom-gold-big'
    | 'custom-light';
  className?: string;
  children?: ReactNode;
  typographyClassName?: string;
  loading?: boolean;
  typeLoading?: 'dots' | 'chips';
  customButtonSrc?: string;
  customButtonWidth?: number;
  customButtonHeight?: number;
}

export const Button: FC<ButtonProps> = ({
  title,
  onClick,
  variant,
  disabled = false,
  shape = 'default',
  className,
  children,
  typographyClassName,
  loading = false,
  typeLoading = 'chips',
  customButtonSrc,
  customButtonWidth = 122,
  customButtonHeight = 39,
  ...rest
}) => {
  const buttonClassName = cx(
    styles[shape],
    styles[variant],
    styles.btnContainer,
    {
      [styles.disabledBtnContainer]: disabled,
    },
    className,
  );
  const textClassName = variant === 'filled' ? styles.filledText : styles.outlinedText;

  const handleClick = () => {
    if (loading || disabled) return;
    onClick();
  };

  if (
    variant === 'custom' ||
    variant === 'custom-gold' ||
    variant === 'custom-light' ||
    variant === 'custom-gold-big' ||
    variant === 'custom-stretchable'
  )
    return (
      <div
        onClick={handleClick}
        className={cx(className, styles.customBtnContainer, {
          [styles.disabled]: disabled,
        })}
        {...rest}
      >
        {customButtonSrc ? (
          <img
            alt="customButton"
            src={customButtonSrc}
            width={customButtonWidth}
            height={customButtonHeight}
          />
        ) : variant === 'custom-gold-big' ? (
          <BigGoldButtonSvg
            disabled={disabled}
            width={customButtonWidth}
            height={customButtonHeight}
          />
        ) : variant === 'custom-stretchable' ? (
          <div className={styles.customBtnSvgButton}></div>
        ) : (
          <ButtonSvg
            disabled={disabled}
            width={customButtonWidth}
            height={customButtonHeight}
            gradient={
              variant === 'custom-gold'
                ? 'gold'
                : variant === 'custom-light'
                  ? 'light-green'
                  : 'default'
            }
          />
        )}

        {loading && typeLoading === 'dots' && (
          <div className={styles.customBtnContent}>
            <AnimatedDotsText text="" variantText="primary" />
          </div>
        )}
        {loading && typeLoading === 'chips' && <ChipsAnimated disabled={disabled} />}
        {!loading && (
          <Typography
            component="h1"
            variant={
              variant === 'custom-gold' || variant === 'custom-gold-big'
                ? 'tertiary'
                : 'primary'
            }
            style={{ height: customButtonHeight }}
            className={cx(
              styles.customBtnContent,
              typographyClassName,
              {
                [styles.disabledBtnText]: disabled,
              },
              styles[variant],
            )}
          >
            {children ?? title}
          </Typography>
        )}
      </div>
    );

  return (
    <button
      disabled={disabled}
      onClick={() => {
        if (!loading) onClick();
      }}
      className={buttonClassName}
    >
      {loading ? (
        <AnimatedDotsText text="" classNameText={styles.loadingDots} />
      ) : (
        <Typography
          component={variant === 'text' ? 'h3' : 'h2'}
          className={cx(textClassName, typographyClassName, {
            [styles.disabledBtnText]: disabled,
          })}
        >
          {children || title}
        </Typography>
      )}
    </button>
  );
};

const ChipsAnimated: FC<{ disabled: boolean }> = ({ disabled }) => {
  return (
    <div className={styles.customBtnContent}>
      <ChipBingoIcon
        className={cx(styles.rotatingChip, {
          [styles.rotatingChipDisabled]: disabled,
        })}
      />
      <ChipBingoIcon
        className={cx(styles.rotatingChip, {
          [styles.rotatingChipDisabled]: disabled,
        })}
      />
      <ChipBingoIcon
        className={cx(styles.rotatingChip, {
          [styles.rotatingChipDisabled]: disabled,
        })}
      />
    </div>
  );
};
